import { recordClientLog } from './clientLog';
import { addBreadcrumb } from '../lib/sentry';
import { hasConsent } from '../lib/consent';
import { TELEMETRY_URL } from './api';

export type JarvisEventType =
  | 'UI_ERROR'
  | 'API_ERROR'
  | 'WS_ERROR'
  | 'PERF'
  | 'SESSION'
  | 'RATE_LIMIT'
  | 'AUTH'
  | 'TELEPROMPTER'
  | 'BILLING'
  | 'CAPTURE'
  | 'LANDING'
  | 'DASHBOARD';

export interface TelemetryEvent<T extends string = JarvisEventType> {
  type: T;
  payload: Record<string, unknown>;
  timestamp: string;
}

interface TelemetryConfig {
  appName: string;
  appVersion: string;
  maxBuffer: number;
  flushIntervalMs: number;
  endpoint: string;
}

let config: TelemetryConfig = {
  appName: 'jarvis',
  appVersion: 'v5.8.222',
  maxBuffer: 50,
  flushIntervalMs: 30000,
  // In Electron (file:) TELEMETRY_URL e' il dominio assoluto; sul web e' '' (same-origin).
  endpoint: resolveTelemetryEndpoint(TELEMETRY_URL),
};

let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Endpoint del flush batch. In Electron base e' il dominio assoluto (TELEMETRY_URL);
// sul web e' '' per restare same-origin. Attenzione: un path relativo in pacchetto
// (file:) diventa file:///C:/api/telemetry -> ERR_FILE_NOT_FOUND.
export function resolveTelemetryEndpoint(base: string): string {
  return `${base}/api/telemetry`;
}

export function initTelemetry(cfg: Partial<TelemetryConfig>): void {
  config = { ...config, ...cfg };
}

export function track(type: JarvisEventType, payload: Record<string, unknown>): void {
  // Gating GDPR reale: senza consenso analytics non registriamo/Trasmettiamo nulla.
  if (!hasConsent('analytics')) return;

  recordClientLog('info', `[track] ${type}`, { type, ...payload });

  try {
    addBreadcrumb(`[track] ${type}`, payload, 'telemetry');
  } catch {
    // Sentry non inizializzato
  }

  const entry: TelemetryEvent = {
    type,
    payload,
    timestamp: new Date().toISOString(),
  };

  try {
    const key = `${config.appName}_telemetry`;
    const existing = JSON.parse(sessionStorage.getItem(key) || '[]');
    existing.push(entry);
    if (existing.length > config.maxBuffer) existing.shift();
    sessionStorage.setItem(key, JSON.stringify(existing));
  } catch {
    // sessionStorage not available
  }

  if (!flushTimer) {
    flushTimer = setTimeout(() => { flush(); flushTimer = null; }, config.flushIntervalMs);
  }
}

async function flush(): Promise<void> {
  try {
    if (!hasConsent('analytics')) return;
    const buffer = getTelemetryBuffer();
    if (buffer.length === 0) return;
    clearTelemetry();
    await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // REQ-013: il server richiede il consenso esplicito nel body (gate fail-closed).
      body: JSON.stringify({ app: config.appName, version: config.appVersion, consent: hasConsent('analytics'), events: buffer }),
    }).catch(() => {});
  } catch {
    // best-effort
  }
}

export function getTelemetryBuffer(): TelemetryEvent[] {
  try {
    const key = `${config.appName}_telemetry`;
    return JSON.parse(sessionStorage.getItem(key) || '[]');
  } catch {
    return [];
  }
}

export function clearTelemetry(): void {
  try {
    const key = `${config.appName}_telemetry`;
    sessionStorage.removeItem(key);
  } catch {
    // no-op
  }
}

// Best-effort stop alla revoca del consenso: annulla il flush pendente e svuota il buffer.
// `track()` e `flush()` restano comunque gated da hasConsent('analytics').
export function stopTelemetry(): void {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  clearTelemetry();
}

export function trackLatency(metric: string, durationMs: number, extra?: Record<string, unknown>): void {
  track('PERF', { metric, durationMs, exceedsThreshold: durationMs > 3000, ...extra });
}

export function trackRateLimit(endpoint: string): void {
  track('RATE_LIMIT', { endpoint });
}